import { useState, useEffect } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Alert from '@mui/material/Alert'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import { checkPolicy, getUserContext, listResourceTypes, type CheckResult, type UserContext, type ResourceType } from '../services/authApi'

const ACTIONS = ['create', 'read', 'update', 'delete', 'publish', 'share', 'use']

export function CheckTesterPage() {
  const [resourceTypes, setResourceTypes] = useState<ResourceType[]>([])
  const [userId, setUserId] = useState('')
  const [rdId, setRdId] = useState('')
  const [actorType, setActorType] = useState('user')
  const [agentId, setAgentId] = useState('')
  const [resourceType, setResourceType] = useState('study')
  const [action, setAction] = useState('read')

  const [result, setResult] = useState<CheckResult | null>(null)
  const [context, setContext] = useState<UserContext | null>(null)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    listResourceTypes().then(setResourceTypes).catch(() => {})
  }, [])

  async function handleCheck() {
    setError('')
    setResult(null)
    setContext(null)
    setLoading(true)

    try {
      const [checkResult, contextResult] = await Promise.all([
        checkPolicy({ userId, rdId, actorType, resourceType, action, agentId: agentId || undefined }),
        getUserContext(userId),
      ])
      setResult(checkResult)
      setContext(contextResult)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Check failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box>
      <Typography variant="h3" sx={{ mb: 1 }}>Authorization Check Tester</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Test the Flavor 1 policy check against the live auth service.
      </Typography>

      <Paper sx={{ border: '1px solid', borderColor: 'divider', p: 3, mb: 3 }} elevation={0}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <TextField
            fullWidth label="User ID" value={userId}
            onChange={e => setUserId(e.target.value)}
            placeholder="Cognito sub or test-user-123"
          />
          <TextField
            fullWidth label="Research Domain ID" value={rdId}
            onChange={e => setRdId(e.target.value)}
            placeholder="Research Domain UUID"
          />

          <Box>
            <Typography variant="caption" color="text.secondary">Actor Type</Typography>
            <Select fullWidth size="small" value={actorType} onChange={e => setActorType(e.target.value)}>
              <MenuItem value="user">User (human)</MenuItem>
              <MenuItem value="agent">Agent (AI)</MenuItem>
            </Select>
          </Box>

          {actorType === 'agent' && (
            <TextField
              fullWidth label="Agent ID (optional)" value={agentId}
              onChange={e => setAgentId(e.target.value)}
              placeholder="registered agent id — resolves its trust tier"
              helperText="If set, the agent's tier policy applies (falls back to bare agent)"
            />
          )}

          <Box>
            <Typography variant="caption" color="text.secondary">Resource Type</Typography>
            <Select fullWidth size="small" value={resourceType} onChange={e => setResourceType(e.target.value)}>
              {resourceTypes.map(rt => <MenuItem key={rt.key} value={rt.key}>{rt.label} ({rt.key})</MenuItem>)}
            </Select>
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary">Action</Typography>
            <Select fullWidth size="small" value={action} onChange={e => setAction(e.target.value)}>
              {ACTIONS.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
            </Select>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
            <Button
              fullWidth variant="contained"
              startIcon={<PlayArrowIcon />}
              onClick={handleCheck}
              disabled={loading || !userId || !rdId}
            >
              {loading ? 'Checking...' : 'Run Check'}
            </Button>
          </Box>
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {result && (
        <Paper sx={{ border: '1px solid', borderColor: 'divider', p: 3 }} elevation={0}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Chip
              label={result.allowed ? 'ALLOWED' : 'DENIED'}
              color={result.allowed ? 'success' : 'error'}
              sx={{ fontWeight: 700, fontSize: '0.875rem' }}
            />
            <Typography variant="body2" color="text.secondary">
              {result.reason}
            </Typography>
          </Box>

          {result.role && (
            <Typography variant="body2">
              Resolved role: <Chip label={result.role} size="small" variant="outlined" />
            </Typography>
          )}

          {context && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="overline" sx={{ mb: 1, display: 'block' }}>User Context</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                <Typography variant="body2">RD: <code>{context.rdId || 'none'}</code></Typography>
                <Typography variant="body2">Role: <code>{context.role || 'none'}</code></Typography>
                <Typography variant="body2">Groups: <code>{context.groups.length > 0 ? context.groups.join(', ') : 'none'}</code></Typography>
                <Typography variant="body2">
                  System Admin: {context.isSystemAdmin
                    ? <Chip label="Yes" size="small" color="error" />
                    : <Chip label="No" size="small" variant="outlined" />}
                </Typography>
              </Box>
            </>
          )}
        </Paper>
      )}
    </Box>
  )
}
